// Defaults and validation for the System Punch Processing settings screen.
// Defaults match the system endpoint's is_default response. Reset stages
// these values for saving. Organisation settings have their own defaults.

import { toSeconds } from "services/utils/durationUnits";
export { toSeconds } from "services/utils/durationUnits";

const duration = (value, unit) => ({ value: String(value), unit });

// ── Global settings (System Punch Processing) ──────────────────────────────
// Fallback unit choices. The system endpoint sends the allowed units and the
// screen uses those when present.
export const INITIAL_DELAY_UNITS = ["seconds", "minutes", "hours"];
export const RETRY_AGE_UNITS = ["hours", "days"];

const retryDefaults = () => ({
  maxAttempts: "5",
  initialDelay: duration(1, "minutes"),
  maxDelay: duration(30, "minutes"),
  maxAge: duration(24, "hours"),
});

export const buildSystemDefaults = () => ({
  dispatcherRetry: retryDefaults(),
  attendanceRetry: retryDefaults(),
  quarantineAfterRetryLimit: true,
  retention: {
    hot: duration(180, "days"),
    archive: duration(365, "days"),
    rawPayload: duration(60, "days"),
    technicalAudit: duration(365, "days"),
  },
  monitoring: {
    deviceInactivity: duration(30, "minutes"),
    deviceOffline: duration(60, "minutes"),
    clockDrift: duration(2, "minutes"),
    eventFlowInactivity: duration(30, "minutes"),
  },
});

// ── Validation ─────────────────────────────────────────────────────────────
const MAX_VALUE = 100000;

// "" when valid, otherwise the message to show under the field.
export const wholeNumberError = (raw, { min = 1, max = MAX_VALUE } = {}) => {
  const text = String(raw ?? "").trim();
  if (!text) return "This field is required.";
  if (!/^\d+$/.test(text)) return "Enter a whole number.";
  const number = Number(text);
  if (number < min) return `Enter ${min} or more.`;
  if (number > max) return `Enter ${max} or less.`;
  return "";
};

const addError = (errors, key, message) => {
  if (message) errors[key] = message;
};

const validateRetry = (errors, prefix, retry) => {
  addError(errors, `${prefix}.maxAttempts`, wholeNumberError(retry.maxAttempts, { max: 100 }));
  // The API takes each duration as a whole number in its own unit.
  ["initialDelay", "maxDelay", "maxAge"].forEach(field =>
    addError(errors, `${prefix}.${field}`, wholeNumberError(retry[field].value)),
  );

  if (!errors[`${prefix}.initialDelay`] && !errors[`${prefix}.maxDelay`]) {
    if (toSeconds(retry.maxDelay) < toSeconds(retry.initialDelay)) {
      errors[`${prefix}.maxDelay`] = "Must not be shorter than the initial retry delay.";
    }
  }
  if (!errors[`${prefix}.maxDelay`] && !errors[`${prefix}.maxAge`]) {
    if (toSeconds(retry.maxAge) < toSeconds(retry.maxDelay)) {
      errors[`${prefix}.maxAge`] = "Must not be shorter than the maximum retry delay.";
    }
  }
};

// Returns { fieldKey: message }; empty when the form is valid.
export const validateSystemSettings = (form) => {
  const errors = {};
  validateRetry(errors, "dispatcherRetry", form.dispatcherRetry);
  validateRetry(errors, "attendanceRetry", form.attendanceRetry);

  Object.entries(form.retention).forEach(([key, value]) =>
    addError(errors, `retention.${key}`, wholeNumberError(value.value)),
  );
  // Archived events cannot be dropped before they leave the primary database.
  if (!errors["retention.hot"] && !errors["retention.archive"]) {
    if (toSeconds(form.retention.archive) < toSeconds(form.retention.hot)) {
      errors["retention.archive"] = "Must not be shorter than the hot retention.";
    }
  }

  Object.entries(form.monitoring).forEach(([key, value]) =>
    addError(errors, `monitoring.${key}`, wholeNumberError(value.value)),
  );
  return errors;
};
